import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://zpanel.randuapp.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/sign-in';
    }
    return Promise.reject(error);
  }
);

export const adminService = {
  login: async (email, password) => {
    const response = await api.post('/login', { email, password });
    return response.data;
  },
};

export const dashboardService = {
  getStats: async () => {
    try {
      const response = await api.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('❌ getStats API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'İstatistikler alınamadı'
      };
    }
  },
  getRevenueTrend: async (startDate, endDate, period) => {
    try {
      const params = { period: period || 'Monthly' };
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const response = await api.get('/dashboard/revenue-trend', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getRevenueTrend API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Gelir trendi alınamadı'
      };
    }
  },
  getAppointmentStatus: async () => {
    try {
      const response = await api.get('/dashboard/appointment-status');
      return response.data;
    } catch (error) {
      console.error('❌ getAppointmentStatus API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Randevu durumu alınamadı'
      };
    }
  },
  getRecentOrganizations: async () => {
    try {
      const response = await api.get('/dashboard/recent-organizations');
      return response.data;
    } catch (error) {
      console.error('❌ getRecentOrganizations API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Organizasyonlar alınamadı'
      };
    }
  },
};

export const usersService = {
  getAllUsers: async (params = {}) => {
    try {
      const response = await api.get('/users', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllUsers API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Kullanıcılar alınamadı'
      };
    }
  },
  getUsersByRole: async (role, params = {}) => {
    try {
      const response = await api.get(`/users/role/${role}`, { params });
      return response.data;
    } catch (error) {
      console.error('❌ getUsersByRole API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Kullanıcılar alınamadı'
      };
    }
  },
  getUserOrganizations: async (userId) => {
    try {
      const response = await api.get(`/users/${userId}/organizations`);
      return response.data;
    } catch (error) {
      console.error('❌ getUserOrganizations API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Organizasyonlar alınamadı'
      };
    }
  },
  updateUser: async (userId, data) => {
    try {
      const response = await api.put(`/users/${userId}`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updateUser API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Kullanıcı güncellenemedi'
      };
    }
  },
  toggleUserStatus: async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/toggle-status`);
      return response.data;
    } catch (error) {
      console.error('❌ toggleUserStatus API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Kullanıcı durumu değiştirilemedi'
      };
    }
  },
  assignUserToOrganization: async (userId, organizationId, roleId) => {
    try {
      const response = await api.post(`/users/${userId}/assign`, { organizationId, roleId });
      return response.data;
    } catch (error) {
      console.error('❌ assignUserToOrganization API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Kullanıcı atanamadı'
      };
    }
  },
  resetUserPassword: async (userId, newPassword) => {
    try {
      const response = await api.post(`/users/${userId}/reset-password`, { newPassword });
      return response.data;
    } catch (error) {
      console.error('❌ resetUserPassword API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Şifre sıfırlanamadı'
      };
    }
  },
  getAllOrganizations: async () => {
    try {
      const response = await api.get('/organizations');
      return response.data;
    } catch (error) {
      console.error('❌ getAllOrganizations API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Organizasyonlar alınamadı'
      };
    }
  },
};

export const organizationsService = {
  getAllOrganizations: async (params = {}) => {
    try {
      const response = await api.get('/organizations/list', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllOrganizations API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Organizasyonlar alınamadı'
      };
    }
  },
  getOrganizationsByStatus: async (status, params = {}) => {
    try {
      const response = await api.get(`/organizations/status/${status}`, { params });
      return response.data;
    } catch (error) {
      console.error('❌ getOrganizationsByStatus API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Organizasyonlar alınamadı'
      };
    }
  },
  getOrganization: async (organizationId) => {
    try {
      const response = await api.get(`/organizations/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('❌ getOrganization API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Organizasyon alınamadı'
      };
    }
  },
  updateOrganization: async (organizationId, data) => {
    try {
      const response = await api.put(`/organizations/${organizationId}`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updateOrganization API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Organizasyon güncellenemedi'
      };
    }
  },
  toggleOrganizationStatus: async (organizationId) => {
    try {
      const response = await api.post(`/organizations/${organizationId}/toggle-status`);
      return response.data;
    } catch (error) {
      console.error('❌ toggleOrganizationStatus API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Organizasyon durumu değiştirilemedi'
      };
    }
  },
  toggleOrganizationListedStatus: async (organizationId) => {
    try {
      const response = await api.post(`/organizations/${organizationId}/toggle-listed-status`);
      return response.data;
    } catch (error) {
      console.error('❌ toggleOrganizationListedStatus API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Vitrin durumu değiştirilemedi'
      };
    }
  },
  toggleSubscriptionStatus: async (organizationId) => {
    try {
      const response = await api.post(`/organizations/${organizationId}/toggle-subscription-status`);
      return response.data;
    } catch (error) {
      console.error('❌ toggleSubscriptionStatus API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Abonelik durumu değiştirilemedi'
      };
    }
  },
  getOrganizationSubscription: async (organizationId) => {
    try {
      const response = await api.get(`/organizations/${organizationId}/subscription`);
      return response.data;
    } catch (error) {
      console.error('❌ getOrganizationSubscription API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Abonelik bilgileri alınamadı'
      };
    }
  },
  getOrganizationLimits: async (organizationId) => {
    try {
      const response = await api.get(`/organizations/${organizationId}/limits`);
      return response.data;
    } catch (error) {
      console.error('❌ getOrganizationLimits API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Limit bilgileri alınamadı'
      };
    }
  },
  updateOrganizationSubscription: async (organizationId, data) => {
    try {
      const response = await api.put(`/organizations/${organizationId}/subscription`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updateOrganizationSubscription API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Abonelik paketi güncellenemedi'
      };
    }
  },
  updateSubscriptionEndDate: async (organizationId, endsAt) => {
    try {
      const response = await api.put(`/organizations/${organizationId}/subscription/end-date`, { endsAt });
      return response.data;
    } catch (error) {
      console.error('❌ updateSubscriptionEndDate API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Bitiş tarihi güncellenemedi'
      };
    }
  },
  updateOrganizationLimits: async (organizationId, data) => {
    try {
      const response = await api.put(`/organizations/${organizationId}/limits`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updateOrganizationLimits API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Limitler güncellenemedi'
      };
    }
  },
  getOrganizationCategories: async (organizationId) => {
    try {
      const response = await api.get(`/organizations/${organizationId}/categories`);
      return response.data;
    } catch (error) {
      console.error('❌ getOrganizationCategories API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Kategoriler alınamadı'
      };
    }
  },
  downloadBulkTemplate: async () => {
    try {
      const response = await api.get('/organizations/bulk/template', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'organizations-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      console.error('❌ downloadBulkTemplate API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Şablon indirilemedi'
      };
    }
  },
  previewBulkUpload: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/organizations/bulk/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('❌ previewBulkUpload API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Önizleme oluşturulamadı'
      };
    }
  },
  bulkUploadOrganizations: async (file, options = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (options.publish !== undefined) {
        formData.append('publish', options.publish ? 'true' : 'false');
      }
      const response = await api.post('/organizations/bulk/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('❌ bulkUploadOrganizations API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Toplu yükleme başarısız'
      };
    }
  },
  createListedOrganization: async (data) => {
    try {
      const response = await api.post('/organizations/listed', data);
      return response.data;
    } catch (error) {
      console.error('❌ createListedOrganization API error:', error);
      const message = error.response?.data?.message;
      const detail = error.response?.data?.error;
      return {
        success: false,
        error: detail ? `${message || 'Organizasyon eklenemedi'} (${detail})` : (message || error.message || 'Organizasyon eklenemedi')
      };
    }
  },
  getCities: async () => {
    try {
      const response = await api.get('/organizations/locations/cities');
      return response.data;
    } catch (error) {
      console.error('❌ getCities API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Şehirler alınamadı'
      };
    }
  },
  getDistrictsByCity: async (cityId) => {
    try {
      const response = await api.get('/organizations/locations/districts', {
        params: { city_id: cityId }
      });
      return response.data;
    } catch (error) {
      console.error('❌ getDistrictsByCity API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'İlçeler alınamadı'
      };
    }
  },
  checkPhoneAvailability: async (phone) => {
    try {
      const response = await api.get('/organizations/check-phone', {
        params: { phone }
      });
      return response.data;
    } catch (error) {
      console.error('❌ checkPhoneAvailability API error:', error);
      return {
        success: false,
        available: false,
        error: error.response?.data?.message || error.message || 'Telefon kontrolü yapılamadı'
      };
    }
  },
  getOrganizationAuthorizations: async (organizationId, params = {}) => {
    try {
      const response = await api.get(`/organizations/${organizationId}/authorizations`, { params });
      return response.data;
    } catch (error) {
      console.error('❌ getOrganizationAuthorizations API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Yetkilendirmeler alınamadı'
      };
    }
  },
  checkAuthorizationPhone: async (organizationId, phone, group) => {
    try {
      const response = await api.get(`/organizations/${organizationId}/authorizations/check-phone`, {
        params: { phone, group }
      });
      return response.data;
    } catch (error) {
      console.error('❌ checkAuthorizationPhone API error:', error);
      return {
        success: false,
        canAdd: false,
        error: error.response?.data?.message || error.message || 'Telefon kontrolü yapılamadı'
      };
    }
  },
  createOrganizationAuthorization: async (organizationId, payload) => {
    try {
      const response = await api.post(`/organizations/${organizationId}/authorizations`, payload);
      return response.data;
    } catch (error) {
      console.error('❌ createOrganizationAuthorization API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Yetkilendirme eklenemedi'
      };
    }
  },
};

export const plansService = {
  getAllPlans: async () => {
    try {
      const response = await api.get('/plans');
      return response.data;
    } catch (error) {
      console.error('❌ getAllPlans API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Planlar alınamadı'
      };
    }
  },
  getPlanStats: async () => {
    try {
      const response = await api.get('/plans/stats');
      return response.data;
    } catch (error) {
      console.error('❌ getPlanStats API error:', error);
      return {
        success: false,
        data: [],
        summary: null,
        error: error.response?.data?.message || error.message || 'Plan istatistikleri alınamadı'
      };
    }
  },
  createPlan: async (payload) => {
    try {
      const response = await api.post('/plans', payload);
      return response.data;
    } catch (error) {
      console.error('❌ createPlan API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Plan oluşturulamadı'
      };
    }
  },
  updatePlan: async (planId, payload) => {
    try {
      const response = await api.put(`/plans/${planId}`, payload);
      return response.data;
    } catch (error) {
      console.error('❌ updatePlan API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Plan güncellenemedi'
      };
    }
  },
};

export const invoicesService = {
  getAllInvoices: async (params = {}) => {
    try {
      const response = await api.get('/invoices', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllInvoices API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Faturalar alınamadı'
      };
    }
  },
  getInvoice: async (id) => {
    try {
      const response = await api.get(`/invoices/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ getInvoice API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Fatura detayı alınamadı'
      };
    }
  },
  updateInvoiceStatus: async (id, status) => {
    try {
      const response = await api.put(`/invoices/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('❌ updateInvoiceStatus API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Fatura durumu güncellenemedi'
      };
    }
  },
  deleteInvoice: async (id) => {
    try {
      const response = await api.delete(`/invoices/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ deleteInvoice API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Fatura silinemedi'
      };
    }
  },
};

export const popupsService = {
  getAllPopups: async (params = {}) => {
    try {
      const response = await api.get('/popups', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllPopups API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Popuplar alınamadı'
      };
    }
  },
  getPopup: async (id) => {
    try {
      const response = await api.get(`/popups/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ getPopup API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Popup detayı alınamadı'
      };
    }
  },
  createPopup: async (data) => {
    try {
      const response = await api.post('/popups', data);
      return response.data;
    } catch (error) {
      console.error('❌ createPopup API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Popup oluşturulamadı'
      };
    }
  },
  updatePopup: async (id, data) => {
    try {
      const response = await api.put(`/popups/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updatePopup API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Popup güncellenemedi'
      };
    }
  },
  deletePopup: async (id) => {
    try {
      const response = await api.delete(`/popups/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ deletePopup API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Popup silinemedi'
      };
    }
  },
  calculateFilterCount: async (filters) => {
    try {
      const response = await api.post('/popups/calculate-filter-count', { filters });
      return response.data;
    } catch (error) {
      console.error('❌ calculateFilterCount API error:', error);
      return {
        success: false,
        count: 0,
        error: error.response?.data?.message || error.message || 'Filtre sayısı hesaplanamadı'
      };
    }
  },
  getFilterOptions: async () => {
    try {
      const response = await api.get('/popups/filter-options');
      return response.data;
    } catch (error) {
      console.error('❌ getFilterOptions API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Filtre seçenekleri alınamadı'
      };
    }
  },
  uploadImage: async (popupId, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await api.post(`/popups/${popupId}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('❌ uploadPopupImage API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Görsel yüklenemedi'
      };
    }
  },
};

export const partnersService = {
  getAllPartnerApplications: async (params = {}) => {
    try {
      const response = await api.get('/partners/applications', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllPartnerApplications API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Partner başvuruları alınamadı'
      };
    }
  },
  getAllPartners: async (params = {}) => {
    try {
      const response = await api.get('/partners', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllPartners API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Partner\'ler alınamadı'
      };
    }
  },
  getPartner: async (partnerId) => {
    try {
      const response = await api.get(`/partners/${partnerId}`);
      return response.data;
    } catch (error) {
      console.error('❌ getPartner API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Partner alınamadı'
      };
    }
  },
  approvePartnerApplication: async (applicationId) => {
    try {
      const response = await api.post(`/partners/applications/${applicationId}/approve`);
      return response.data;
    } catch (error) {
      console.error('❌ approvePartnerApplication API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Partner başvurusu onaylanamadı'
      };
    }
  },
  rejectPartnerApplication: async (applicationId, rejectionReason) => {
    try {
      const response = await api.post(`/partners/applications/${applicationId}/reject`, {
        rejection_reason: rejectionReason
      });
      return response.data;
    } catch (error) {
      console.error('❌ rejectPartnerApplication API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Partner başvurusu reddedilemedi'
      };
    }
  },
  togglePartnerStatus: async (partnerId) => {
    try {
      const response = await api.post(`/partners/${partnerId}/toggle-status`);
      return response.data;
    } catch (error) {
      console.error('❌ togglePartnerStatus API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Partner durumu değiştirilemedi'
      };
    }
  },
  updatePartner: async (partnerId, data) => {
    try {
      const response = await api.put(`/partners/${partnerId}`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updatePartner API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Partner güncellenemedi'
      };
    }
  },
  deletePartner: async (partnerId) => {
    try {
      const response = await api.delete(`/partners/${partnerId}`);
      return response.data;
    } catch (error) {
      console.error('❌ deletePartner API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Partner silinemedi'
      };
    }
  },
};

export const contractsService = {
  getAllContracts: async (params = {}) => {
    try {
      const response = await api.get('/contracts', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllContracts API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Sözleşmeler alınamadı'
      };
    }
  },
  getContract: async (contractId) => {
    try {
      const response = await api.get(`/contracts/${contractId}`);
      return response.data;
    } catch (error) {
      console.error('❌ getContract API error:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Sözleşme alınamadı'
      };
    }
  },
  createContract: async (data) => {
    try {
      const response = await api.post('/contracts', data);
      return response.data;
    } catch (error) {
      console.error('❌ createContract API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Sözleşme oluşturulamadı'
      };
    }
  },
  updateContract: async (contractId, data) => {
    try {
      const response = await api.put(`/contracts/${contractId}`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updateContract API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Sözleşme güncellenemedi'
      };
    }
  },
  deleteContract: async (contractId) => {
    try {
      const response = await api.delete(`/contracts/${contractId}`);
      return response.data;
    } catch (error) {
      console.error('❌ deleteContract API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Sözleşme silinemedi'
      };
    }
  },
  toggleContractStatus: async (contractId) => {
    try {
      const response = await api.post(`/contracts/${contractId}/toggle-status`);
      return response.data;
    } catch (error) {
      console.error('❌ toggleContractStatus API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Sözleşme durumu değiştirilemedi'
      };
    }
  },
};

export const categoriesService = {
  getAllCategories: async (params = {}) => {
    try {
      const response = await api.get('/categories', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllCategories API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Kategoriler alınamadı'
      };
    }
  },
  getAllFlat: async () => {
    try {
      const response = await api.get('/categories/all');
      return response.data;
    } catch (error) {
      console.error('❌ getAllFlat API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Kategoriler alınamadı'
      };
    }
  },
  getCategory: async (categoryId) => {
    try {
      const response = await api.get(`/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('❌ getCategory API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Kategori alınamadı'
      };
    }
  },
  createCategory: async (data) => {
    try {
      const response = await api.post('/categories', data);
      return response.data;
    } catch (error) {
      console.error('❌ createCategory API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Kategori oluşturulamadı'
      };
    }
  },
  updateCategory: async (categoryId, data) => {
    try {
      const response = await api.put(`/categories/${categoryId}`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updateCategory API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Kategori güncellenemedi'
      };
    }
  },
  deleteCategory: async (categoryId) => {
    try {
      const response = await api.delete(`/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      console.error('❌ deleteCategory API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Kategori silinemedi'
      };
    }
  },
};

export const customersService = {
  getAllPotentialCustomers: async (params = {}) => {
    try {
      const response = await api.get('/customers/potential', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllPotentialCustomers API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Potansiyel müşteriler alınamadı'
      };
    }
  },
};

export const bannersService = {
  getAllBanners: async (params = {}) => {
    try {
      const response = await api.get('/banners', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllBanners API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Banner\'lar alınamadı'
      };
    }
  },
  getBanner: async (bannerId) => {
    try {
      const response = await api.get(`/banners/${bannerId}`);
      return response.data;
    } catch (error) {
      console.error('❌ getBanner API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Banner alınamadı'
      };
    }
  },
  createBanner: async (formData) => {
    try {
      const response = await api.post('/banners', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('❌ createBanner API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Banner oluşturulamadı'
      };
    }
  },
  updateBanner: async (bannerId, formData) => {
    try {
      const response = await api.put(`/banners/${bannerId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('❌ updateBanner API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Banner güncellenemedi'
      };
    }
  },
  deleteBanner: async (bannerId) => {
    try {
      const response = await api.delete(`/banners/${bannerId}`);
      return response.data;
    } catch (error) {
      console.error('❌ deleteBanner API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Banner silinemedi'
      };
    }
  },
};

export const documentsService = {
  getAllDocuments: async (params = {}) => {
    try {
      const response = await api.get('/documents', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllDocuments API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Dökümanlar alınamadı'
      };
    }
  },
  uploadDocument: async (formData) => {
    try {
      const response = await api.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('❌ uploadDocument API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Döküman yüklenemedi'
      };
    }
  },
  deleteDocument: async (documentId) => {
    try {
      const response = await api.delete(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('❌ deleteDocument API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Döküman silinemedi'
      };
    }
  },
};

export const announcementsService = {
  getAllAnnouncements: async (params = {}) => {
    try {
      const response = await api.get('/announcements', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllAnnouncements API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Duyurular alınamadı'
      };
    }
  },
  getAnnouncement: async (announcementId) => {
    try {
      const response = await api.get(`/announcements/${announcementId}`);
      return response.data;
    } catch (error) {
      console.error('❌ getAnnouncement API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Duyuru alınamadı'
      };
    }
  },
  createAnnouncement: async (data) => {
    try {
      const response = await api.post('/announcements', data);
      return response.data;
    } catch (error) {
      console.error('❌ createAnnouncement API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Duyuru oluşturulamadı'
      };
    }
  },
  updateAnnouncement: async (announcementId, data) => {
    try {
      const response = await api.put(`/announcements/${announcementId}`, data);
      return response.data;
    } catch (error) {
      console.error('❌ updateAnnouncement API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Duyuru güncellenemedi'
      };
    }
  },
  deleteAnnouncement: async (announcementId) => {
    try {
      const response = await api.delete(`/announcements/${announcementId}`);
      return response.data;
    } catch (error) {
      console.error('❌ deleteAnnouncement API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Duyuru silinemedi'
      };
    }
  },
};

export const earningsService = {
  getEarnings: async (params = {}) => {
    try {
      const response = await api.get('/earnings', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getEarnings API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Hak edişler alınamadı'
      };
    }
  },
  getPendingPayments: async (params = {}) => {
    try {
      const response = await api.get('/earnings/pending-payments', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getPendingPayments API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Ödeme bekleyen hak edişler alınamadı'
      };
    }
  },
  getPaidEarnings: async (params = {}) => {
    try {
      const response = await api.get('/earnings/paid', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getPaidEarnings API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Ödenen hak edişler alınamadı'
      };
    }
  },
  getAllEarningsForManagement: async (params = {}) => {
    try {
      const response = await api.get('/earnings/manage', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllEarningsForManagement API error:', error);
      return {
        success: false,
        data: [],
        statistics: {},
        pagination: {},
        error: error.response?.data?.message || error.message || 'Hak ediş yönetim listesi alınamadı'
      };
    }
  },
  getCommissionRates: async () => {
    try {
      const response = await api.get('/earnings/rates');
      return response.data;
    } catch (error) {
      console.error('❌ getCommissionRates API error:', error);
      return {
        success: false,
        data: {},
        error: error.response?.data?.message || error.message || 'Komisyon oranları alınamadı'
      };
    }
  },
  updateCommissionRates: async (data) => {
    try {
      const response = await api.put('/earnings/rates', data);
      return response.data;
    } catch (error) {
      console.error('❌ updateCommissionRates API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Komisyon oranları güncellenemedi'
      };
    }
  },
};

export const messagesService = {
  getMessages: async (params = {}) => {
    try {
      const response = await api.get('/messages', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getMessages API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Mesajlar alınamadı'
      };
    }
  },
  getConversation: async (contactIdentifier, params = {}) => {
    try {
      const response = await api.get(`/messages/conversation/${encodeURIComponent(contactIdentifier)}`, { params });
      return response.data;
    } catch (error) {
      console.error('❌ getConversation API error:', error);
      return {
        success: false,
        data: { messages: [], user: null },
        error: error.response?.data?.message || error.message || 'Konuşma alınamadı'
      };
    }
  },
  sendMessage: async (data) => {
    try {
      const response = await api.post('/messages/send', data);
      return response.data;
    } catch (error) {
      console.error('❌ sendMessage API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Mesaj gönderilemedi'
      };
    }
  },
  archiveMessages: async (data) => {
    try {
      const response = await api.post('/messages/archive', data);
      return response.data;
    } catch (error) {
      console.error('❌ archiveMessages API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Mesajlar arşivlenemedi'
      };
    }
  },
  unarchiveMessages: async (data) => {
    try {
      const response = await api.post('/messages/unarchive', data);
      return response.data;
    } catch (error) {
      console.error('❌ unarchiveMessages API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Mesajlar arşivden çıkarılamadı'
      };
    }
  },
  deleteMessages: async (data) => {
    try {
      const response = await api.post('/messages/delete', data);
      return response.data;
    } catch (error) {
      console.error('❌ deleteMessages API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Mesajlar silinemedi'
      };
    }
  },
  restoreMessages: async (data) => {
    try {
      const response = await api.post('/messages/restore', data);
      return response.data;
    } catch (error) {
      console.error('❌ restoreMessages API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Mesajlar geri yüklenemedi'
      };
    }
  },
  permanentlyDeleteMessages: async (data) => {
    try {
      const response = await api.post('/messages/permanently-delete', data);
      return response.data;
    } catch (error) {
      console.error('❌ permanentlyDeleteMessages API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Mesajlar kalıcı olarak silinemedi'
      };
    }
  },
  getMessageStats: async (params = {}) => {
    try {
      const response = await api.get('/messages/stats', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getMessageStats API error:', error);
      return {
        success: false,
        data: {},
        error: error.response?.data?.message || error.message || 'İstatistikler alınamadı'
      };
    }
  },
};

export const paymentsService = {
  getEligibleEarnings: async (partnerId = '') => {
    try {
      const params = partnerId ? { partner_id: partnerId } : {};
      const response = await api.get('/payments/eligible-earnings', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getEligibleEarnings API error:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || 'Uygun hak edişler alınamadı'
      };
    }
  },
  sendPayment: async (data) => {
    try {
      const response = await api.post('/payments/send', data);
      return response.data;
    } catch (error) {
      console.error('❌ sendPayment API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Ödeme gönderilemedi'
      };
    }
  },
  getAllPayments: async (params = {}) => {
    try {
      const response = await api.get('/payments', { params });
      return response.data;
    } catch (error) {
      console.error('❌ getAllPayments API error:', error);
      return {
        success: false,
        data: [],
        pagination: {},
        error: error.response?.data?.message || error.message || 'Ödemeler alınamadı'
      };
    }
  },
  getPayment: async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}`);
      return response.data;
    } catch (error) {
      console.error('❌ getPayment API error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Ödeme alınamadı'
      };
    }
  },
};

/** Zpanel — Vitrin listeleri (tüm organizasyonlar) */
export const vitrinAdminService = {
  list: async (section, params = {}) => {
    try {
      const path = `/vitrin-admin/${section}`;
      const response = await api.get(path, { params });
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.list', error);
      return {
        success: false,
        data: [],
        pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
        error: error.response?.data?.message || error.message || 'Liste alınamadı'
      };
    }
  },
  patchCustomerReview: async (id, body) => {
    try {
      const response = await api.patch(`/vitrin-admin/customer-reviews/${id}`, body);
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.patchCustomerReview', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Güncellenemedi'
      };
    }
  },
  patchQuestion: async (id, body) => {
    try {
      const response = await api.patch(`/vitrin-admin/questions/${id}`, body);
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.patchQuestion', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Güncellenemedi'
      };
    }
  },
  patchComplaint: async (id, body) => {
    try {
      const response = await api.patch(`/vitrin-admin/complaints/${id}`, body);
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.patchComplaint', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Güncellenemedi'
      };
    }
  },
  patchEditSuggestion: async (id, body) => {
    try {
      const response = await api.patch(`/vitrin-admin/edit-suggestions/${id}`, body);
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.patchEditSuggestion', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Güncellenemedi'
      };
    }
  },
  patchClaim: async (id, body) => {
    try {
      const response = await api.patch(`/vitrin-admin/claims/${id}`, body);
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.patchClaim', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Güncellenemedi'
      };
    }
  },
  patchLead: async (id, body) => {
    try {
      const response = await api.patch(`/vitrin-admin/leads/${id}`, body);
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.patchLead', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Güncellenemedi'
      };
    }
  },
  deleteClaim: async (id) => {
    try {
      const response = await api.delete(`/vitrin-admin/claims/${id}`);
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.deleteClaim', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Silinemedi'
      };
    }
  },
  bulkDeleteClaims: async (ids) => {
    try {
      const response = await api.post('/vitrin-admin/claims/bulk-delete', { ids });
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.bulkDeleteClaims', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Toplu silinemedi'
      };
    }
  },
  bulkStatusClaims: async (ids, status) => {
    try {
      const response = await api.post('/vitrin-admin/claims/bulk-status', { ids, status });
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.bulkStatusClaims', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Durum güncellenemedi'
      };
    }
  },
  deleteEditSuggestion: async (id) => {
    try {
      const response = await api.delete(`/vitrin-admin/edit-suggestions/${id}`);
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.deleteEditSuggestion', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Silinemedi'
      };
    }
  },
  bulkDeleteEditSuggestions: async (ids) => {
    try {
      const response = await api.post('/vitrin-admin/edit-suggestions/bulk-delete', { ids });
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.bulkDeleteEditSuggestions', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Toplu silinemedi'
      };
    }
  },
  bulkStatusEditSuggestions: async (ids, status) => {
    try {
      const response = await api.post('/vitrin-admin/edit-suggestions/bulk-status', { ids, status });
      return response.data;
    } catch (error) {
      console.error('vitrinAdminService.bulkStatusEditSuggestions', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Durum güncellenemedi'
      };
    }
  },
};

export const randuBlogService = {
  getSettings: async () => {
    const response = await api.get('/blog/settings');
    return response.data;
  },
  saveSettings: async (payload) => {
    const response = await api.post('/blog/settings', payload);
    return response.data;
  },
  listPosts: async (params = {}) => {
    const response = await api.get('/blog/posts', { params });
    return response.data;
  },
  getPost: async (id) => {
    const response = await api.get(`/blog/posts/${id}`);
    return response.data;
  },
  createPost: async (payload) => {
    const response = await api.post('/blog/posts', payload);
    return response.data;
  },
  updatePost: async (id, payload) => {
    const response = await api.put(`/blog/posts/${id}`, payload);
    return response.data;
  },
  togglePostActive: async (id) => {
    const response = await api.post(`/blog/posts/${id}/toggle-active`);
    return response.data;
  },
  uploadCover: async (id, file) => {
    const form = new FormData();
    form.append('cover', file);
    const response = await api.post(`/blog/posts/${id}/cover`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  listCustomCategories: async () => {
    const response = await api.get('/blog/custom-categories');
    return response.data;
  },
  createCustomCategory: async (payload) => {
    const response = await api.post('/blog/custom-categories', payload);
    return response.data;
  },
  updateCustomCategory: async (id, payload) => {
    const response = await api.put(`/blog/custom-categories/${id}`, payload);
    return response.data;
  },
  listTags: async () => {
    const response = await api.get('/blog/tags');
    return response.data;
  },
  listJobs: async () => {
    const response = await api.get('/blog/jobs');
    return response.data;
  },
  createJob: async (payload) => {
    const response = await api.post('/blog/jobs', payload);
    return response.data;
  },
  jobLogs: async (jobId) => {
    const response = await api.get(`/blog/jobs/${jobId}/logs`);
    return response.data;
  },
};

export default api;

